/**
 * iHerb Posts page — generate, review, approve/reject Hebrew social posts
 */

import { useState, useCallback } from 'react';
import { useSearchParams } from 'react-router';
import { toast } from '@lib/ui';
import {
  useIherbPosts,
  useGeneratePosts,
  useUpdatePost,
  useDeletePost,
  useSendPost,
  useRefinePost,
  type SortOrder,
} from './hooks/useIherbPosts';
import { GenerateBar } from './components/GenerateBar';
import { PostCard } from './components/PostCard';
import { PostsTable } from './components/PostsTable';
import { BulkActionBar } from './components/BulkActionBar';

type ViewMode = 'card' | 'table';

const DEFAULT_LIMIT = 25;

async function runConcurrent<T>(
  tasks: (() => Promise<T>)[],
  limit: number
): Promise<PromiseSettledResult<T>[]> {
  const results: PromiseSettledResult<T>[] = [];
  let i = 0;
  async function worker() {
    while (i < tasks.length) {
      const idx = i++;
      try {
        results[idx] = { status: 'fulfilled', value: await tasks[idx]() };
      } catch (reason) {
        results[idx] = { status: 'rejected', reason };
      }
    }
  }
  await Promise.all(Array.from({ length: Math.min(limit, tasks.length) }, worker));
  return results;
}

export default function IherbPostsPage() {
  const [searchParams, setSearchParams] = useSearchParams();

  // URL-persisted state
  const sort = (searchParams.get('sort') as SortOrder | null) ?? 'created_at_asc';
  const initialLimit = Math.max(DEFAULT_LIMIT, Number(searchParams.get('n') ?? DEFAULT_LIMIT));
  const hideApproved = searchParams.has('hide_approved');
  const showSent = searchParams.has('show_sent');
  const view: ViewMode = searchParams.get('view') === 'table' ? 'table' : 'card';

  const setParam = useCallback(
    (key: string, value: string | null) => {
      setSearchParams(
        (prev) => {
          const next = new URLSearchParams(prev);
          if (value === null) next.delete(key);
          else next.set(key, value);
          return next;
        },
        { replace: true }
      );
    },
    [setSearchParams]
  );

  // Build exclude param from filter state
  const excludeStatuses = [
    ...(!showSent ? ['sent'] : []),
    ...(hideApproved ? ['approved'] : []),
  ].join(',');

  const {
    posts,
    isLoading,
    isLoadingMore,
    isGenerating,
    hasMore,
    error,
    refetch,
    loadMore,
    startPolling,
  } = useIherbPosts({ sort, initialLimit, exclude: excludeStatuses || undefined });
  const { updatePost, isUpdating } = useUpdatePost();
  const { deletePost, isDeleting } = useDeletePost();
  const { sendPost, isSending } = useSendPost();
  const { generatePosts, isStarting, error: generateError } = useGeneratePosts();
  const { refinePost } = useRefinePost();

  const [selectedIds, setSelectedIds] = useState<Set<string>>(new Set());

  // -------------------------------------------------------------------------
  // Selection
  // -------------------------------------------------------------------------

  const handleToggleSelect = useCallback((id: string) => {
    setSelectedIds((prev) => {
      const next = new Set(prev);
      next.has(id) ? next.delete(id) : next.add(id);
      return next;
    });
  }, []);

  const handleSelectAll = useCallback(() => {
    setSelectedIds(new Set(posts.map((p) => p.id)));
  }, [posts]);

  const handleClearSelection = useCallback(() => {
    setSelectedIds(new Set());
  }, []);

  // -------------------------------------------------------------------------
  // Single-post actions
  // -------------------------------------------------------------------------

  const handleGenerate = async (count: number) => {
    try {
      await generatePosts({ count });
      toast.success(`Generating ${count} posts…`);
      startPolling();
    } catch {
      // error shown in GenerateBar
    }
  };

  const handleApprove = async (id: string) => {
    try {
      await updatePost(id, { status: 'approved' });
      toast.success('Post approved');
      await refetch();
    } catch {
      toast.error('Failed to approve post');
    }
  };

  const handleReject = async (id: string, feedback: string) => {
    try {
      await updatePost(id, { status: 'rejected', feedback: feedback || undefined });
      toast.success('Post rejected');
      await refetch();
    } catch {
      toast.error('Failed to reject post');
    }
  };

  const handleFeedback = async (id: string, feedback: string) => {
    try {
      await updatePost(id, { feedback });
      toast.success('Feedback saved');
      await refetch();
    } catch {
      toast.error('Failed to save feedback');
    }
  };

  const handleSend = async (id: string) => {
    try {
      await sendPost(id);
      toast.success('Post sent');
      await refetch();
    } catch {
      toast.error('Failed to send post');
    }
  };

  const handleRefine = async (id: string): Promise<void> => {
    try {
      await refinePost(id);
      toast.success('Post refined');
      await refetch();
    } catch {
      toast.error('Failed to refine post');
    }
  };

  const handleDelete = async (id: string) => {
    try {
      await deletePost(id);
      setSelectedIds((prev) => {
        const n = new Set(prev);
        n.delete(id);
        return n;
      });
      await refetch();
    } catch {
      toast.error('Failed to delete post');
    }
  };

  const handleLoadMore = async () => {
    await loadMore();
    // Persist the new total in the URL so refresh restores it
    setParam('n', String(posts.length + 25));
  };

  // -------------------------------------------------------------------------
  // Bulk actions
  // -------------------------------------------------------------------------

  const handleBulkApprove = async () => {
    const ids = [...selectedIds];
    const results = await runConcurrent(
      ids.map((id) => () => updatePost(id, { status: 'approved' })),
      6
    );
    const failed = results.filter((r) => r.status === 'rejected').length;
    if (failed) toast.error(`${failed} post(s) failed to approve`);
    else toast.success(`${ids.length} post(s) approved`);
    handleClearSelection();
    await refetch();
  };

  const handleBulkReject = async (feedback: string) => {
    const ids = [...selectedIds];
    const results = await runConcurrent(
      ids.map(
        (id) => () => updatePost(id, { status: 'rejected', feedback: feedback || undefined })
      ),
      6
    );
    const failed = results.filter((r) => r.status === 'rejected').length;
    if (failed) toast.error(`${failed} post(s) failed to reject`);
    else toast.success(`${ids.length} post(s) rejected`);
    handleClearSelection();
    await refetch();
  };

  const handleBulkDelete = async () => {
    const ids = [...selectedIds];
    const results = await runConcurrent(
      ids.map((id) => () => deletePost(id)),
      6
    );
    const failed = results.filter((r) => r.status === 'rejected').length;
    if (failed) toast.error(`${failed} post(s) failed to delete`);
    else toast.success(`${ids.length} post(s) deleted`);
    handleClearSelection();
    await refetch();
  };

  const isWorking = isUpdating || isDeleting;

  return (
    <div className="p-6 space-y-6 pb-24">
      {/* Page header */}
      <div className="flex flex-col gap-4 sm:flex-row sm:items-center sm:justify-between">
        <div>
          <h1 className="text-2xl font-semibold text-gray-900 dark:text-white">iHerb Posts</h1>
          <p className="mt-1 text-gray-500 dark:text-dark-300">
            Generate and review Hebrew WhatsApp/Telegram posts for iHerb Israel products
          </p>
        </div>
        <div className="flex flex-wrap items-center gap-3">
          <button
            onClick={() =>
              setParam('sort', sort === 'created_at_asc' ? 'created_at_desc' : 'created_at_asc')
            }
            className="text-sm text-gray-500 hover:text-gray-700 dark:text-dark-400 dark:hover:text-dark-200 underline underline-offset-2"
          >
            {sort === 'created_at_asc' ? 'Oldest first ↑' : 'Newest first ↓'}
          </button>
          <button
            onClick={() => setParam('hide_approved', hideApproved ? null : '1')}
            className="text-sm text-gray-500 hover:text-gray-700 dark:text-dark-400 dark:hover:text-dark-200 underline underline-offset-2"
          >
            {hideApproved ? 'Show approved' : 'Hide approved'}
          </button>
          <button
            onClick={() => setParam('show_sent', showSent ? null : '1')}
            className="text-sm text-gray-500 hover:text-gray-700 dark:text-dark-400 dark:hover:text-dark-200 underline underline-offset-2"
          >
            {showSent ? 'Hide sent' : 'Show sent'}
          </button>
          <button
            onClick={() => setParam('view', view === 'table' ? null : 'table')}
            className="text-sm text-gray-500 hover:text-gray-700 dark:text-dark-400 dark:hover:text-dark-200 underline underline-offset-2"
          >
            {view === 'table' ? 'Card view' : 'Table view'}
          </button>
          <GenerateBar
            isGenerating={isGenerating}
            isStarting={isStarting}
            error={generateError}
            onGenerate={handleGenerate}
          />
        </div>
      </div>

      {/* Status bar while generating */}
      {isGenerating && (
        <div className="rounded-lg border border-blue-200 bg-blue-50 px-4 py-3 text-sm text-blue-700 dark:border-blue-900 dark:bg-blue-950/40 dark:text-blue-300">
          Generation in progress — posts will appear below as they are ready.
        </div>
      )}

      {/* Error */}
      {error && (
        <div className="rounded-lg border border-red-200 bg-red-50 px-4 py-3 text-sm text-red-700 dark:border-red-900 dark:bg-red-950/40 dark:text-red-300">
          {error}
        </div>
      )}

      {/* Posts grid */}
      {isLoading ? (
        <div className="flex items-center justify-center py-16 text-gray-400 dark:text-dark-400">
          Loading…
        </div>
      ) : posts.length === 0 ? (
        <div className="flex flex-col items-center justify-center py-16 text-gray-400 dark:text-dark-400 space-y-2">
          <p className="text-lg font-medium">No posts</p>
          <p className="text-sm">
            {hideApproved || !showSent
              ? 'Try adjusting the filters above.'
              : 'Choose a count above and click Generate Posts to get started.'}
          </p>
        </div>
      ) : (
        <>
          {view === 'table' ? (
            <PostsTable
              posts={posts}
              selectedIds={selectedIds}
              onToggleSelect={handleToggleSelect}
              onApprove={handleApprove}
              onReject={handleReject}
              onSend={handleSend}
              onDelete={handleDelete}
              isUpdating={isWorking}
              isSending={isSending}
            />
          ) : (
            <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4">
              {posts.map((post) => (
                <PostCard
                  key={post.id}
                  post={post}
                  onApprove={handleApprove}
                  onReject={handleReject}
                  onFeedback={handleFeedback}
                  onDelete={handleDelete}
                  onSend={handleSend}
                  onRefine={handleRefine}
                  isUpdating={isWorking}
                  isSending={isSending}
                  isSelected={selectedIds.has(post.id)}
                  onToggleSelect={handleToggleSelect}
                />
              ))}
            </div>
          )}

          {/* Load more */}
          {hasMore && (
            <div className="flex justify-center pt-2">
              <button
                onClick={handleLoadMore}
                disabled={isLoadingMore}
                className="rounded-lg border border-gray-300 bg-white px-5 py-2 text-sm font-medium text-gray-700 shadow-sm hover:bg-gray-50 disabled:opacity-50 dark:border-dark-600 dark:bg-dark-800 dark:text-dark-200 dark:hover:bg-dark-700"
              >
                {isLoadingMore ? 'Loading…' : 'Load more'}
              </button>
            </div>
          )}
        </>
      )}

      {/* Bulk action bar */}
      {selectedIds.size > 0 && (
        <BulkActionBar
          selectedCount={selectedIds.size}
          totalCount={posts.length}
          onSelectAll={handleSelectAll}
          onClearSelection={handleClearSelection}
          onApprove={handleBulkApprove}
          onReject={handleBulkReject}
          onDelete={handleBulkDelete}
          isWorking={isWorking}
        />
      )}
    </div>
  );
}
